import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// Tipos para os dados de insights
export interface MediaInsight {
    media_id: string;
    media_title: string;
    total_plays: number;
    total_duration_ms: number;
    avg_duration_ms: number;
    last_played_at: string;
    unique_devices: number;
}

export interface TimelinePoint {
    period_start: string;
    total_plays: number;
    unique_medias: number;
}

export interface DeviceInsight {
    device_id: string;
    device_name: string;
    total_plays: number;
    unique_medias: number;
    total_duration_ms: number;
}

export interface InsightsSummary {
    totalPlays: number;
    totalDuration: number;
    activeDevices: number;
    activeMedias: number;
    avgPlaysPerDay: number;
}

export interface UseMediaInsightsReturn {
    // Dados
    mediaInsights: MediaInsight[];
    timeline: TimelinePoint[];
    deviceInsights: DeviceInsight[];
    summary: InsightsSummary;

    // Estado
    loading: boolean;
    error: string | null;

    // Controles
    period: number;
    setPeriod: (days: number) => void;
    refetch: () => Promise<void>;
}

export const useMediaInsights = (userId?: string): UseMediaInsightsReturn => {
    const [mediaInsights, setMediaInsights] = useState<MediaInsight[]>([]);
    const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
    const [deviceInsights, setDeviceInsights] = useState<DeviceInsight[]>([]);
    const [summary, setSummary] = useState<InsightsSummary>({
        totalPlays: 0,
        totalDuration: 0,
        activeDevices: 0,
        activeMedias: 0,
        avgPlaysPerDay: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [period, setPeriod] = useState(30); // Default: 30 dias

    const fetchInsights = useCallback(async (showLoading = true) => {
        if (!userId) {
            setLoading(false);
            return;
        }

        if (showLoading) {
            setLoading(true);
        }
        setError(null);

        try {
            // Buscar insights de mídias
            const { data: mediaData, error: mediaError } = await supabase
                .rpc('get_media_insights', { p_user_id: userId, p_days: period });

            if (mediaError) {
                console.error('Erro ao buscar media insights:', mediaError);
                // Se a função não existir ainda, usar dados vazios
                if (mediaError.code === '42883') {
                    console.log('Função get_media_insights não existe ainda. Execute o SQL.');
                }
            } else if (mediaData) {
                setMediaInsights(mediaData as MediaInsight[]);
            }

            // Buscar timeline
            const { data: timelineData, error: timelineError } = await supabase
                .rpc('get_playback_timeline', {
                    p_user_id: userId,
                    p_days: period,
                    p_group_by: period <= 7 ? 'hour' : 'day'
                });

            if (!timelineError && timelineData) {
                setTimeline(timelineData as TimelinePoint[]);
            }

            // Buscar insights por dispositivo
            const { data: deviceData, error: deviceError } = await supabase
                .rpc('get_device_insights', { p_user_id: userId, p_days: period });

            if (!deviceError && deviceData) {
                setDeviceInsights(deviceData as DeviceInsight[]);
            }

            // Calcular resumo
            const totalPlays = (mediaData as MediaInsight[] || []).reduce((sum, m) => sum + (m.total_plays || 0), 0);
            const totalDuration = (mediaData as MediaInsight[] || []).reduce((sum, m) => sum + (m.total_duration_ms || 0), 0);
            const activeDevices = (deviceData as DeviceInsight[] || []).length;
            const activeMedias = (mediaData as MediaInsight[] || []).length;
            const avgPlaysPerDay = period > 0 ? Math.round(totalPlays / period) : 0;

            setSummary({
                totalPlays,
                totalDuration,
                activeDevices,
                activeMedias,
                avgPlaysPerDay,
            });

        } catch (err) {
            console.error('Erro ao buscar insights:', err);
            setError('Erro ao carregar insights. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }, [userId, period]);

    // Busca inicial
    useEffect(() => {
        fetchInsights();
    }, [fetchInsights]);

    // Supabase Realtime - Atualização automática quando novos logs são inseridos
    useEffect(() => {
        if (!userId) return;

        // Criar canal para escutar inserções na tabela de logs
        const channel = supabase
            .channel(`insights_realtime_${userId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'media_playback_logs',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    console.log('📊 [INSIGHTS] Novo log detectado em tempo real:', payload);
                    // Refresh silencioso (sem mostrar loading spinner)
                    fetchInsights(false);
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('📊 [INSIGHTS] Canal Realtime conectado para userId:', userId);
                }
            });

        // Também fazer polling a cada 30 segundos como fallback
        const pollingInterval = setInterval(() => {
            fetchInsights(false);
        }, 30000);

        return () => {
            supabase.removeChannel(channel);
            clearInterval(pollingInterval);
        };
    }, [userId, fetchInsights]);

    return {
        mediaInsights,
        timeline,
        deviceInsights,
        summary,
        loading,
        error,
        period,
        setPeriod,
        refetch: () => fetchInsights(true),
    };
};

// Função para registrar exibição de mídia (chamada pelo Player)
export const logMediaPlayback = async (params: {
    mediaId: string;
    deviceId?: string;
    screenId?: string;
    userId: string;
    mediaTitle?: string;
    deviceName?: string;
    screenName?: string;
    displayTimeMs: number;
}) => {
    try {
        const { error } = await supabase
            .from('media_playback_logs')
            .insert({
                media_id: params.mediaId,
                device_id: params.deviceId || null,
                screen_id: params.screenId || null,
                user_id: params.userId,
                media_title: params.mediaTitle || null,
                device_name: params.deviceName || null,
                screen_name: params.screenName || null,
                display_time_ms: params.displayTimeMs,
                duration_ms: params.displayTimeMs,
                started_at: new Date().toISOString(),
                ended_at: new Date(Date.now() + params.displayTimeMs).toISOString(),
            });

        if (error) {
            console.error('Erro ao registrar playback:', error);
            return false;
        }

        return true;
    } catch (err) {
        console.error('Erro ao registrar playback:', err);
        return false;
    }
};

export default useMediaInsights;
